import { ReactNode } from 'react';
import cn from 'classnames';
import { Switch } from '@ds';

type IconToggleSize = 'sm' | 'md' | 'lg';

type IconToggleProps = {
  size?: IconToggleSize;
  isSelected?: boolean;
  onValueChange?: (isSelected: boolean) => void;
  enableIcon?: ReactNode;
  disableIcon?: ReactNode;
};

type IconToggleSizeConfig = {
  wrapper: string;
  thumb: string;
  content: string;
};

// Geometry per size: for a track/thumb height of N tailwind units, the selected offset is N+1,
// the pressed width is N+1, the selected+pressed offset is N, and the total width is (2N+1)*4px.
const sizeConfig: Record<IconToggleSize, IconToggleSizeConfig> = {
  sm: {
    wrapper: 'w-[52px] h-6',
    thumb: cn(
      'h-6 w-6 text-base',
      'group-data-[selected=true]:ml-7',
      'group-data-[pressed=true]:w-7',
      'group-data-[selected=true]:group-data-[pressed=true]:ml-6',
    ),
    content: 'text-base',
  },
  md: {
    wrapper: 'w-[60px] h-7',
    thumb: cn(
      'h-7 w-7 text-sm',
      'group-data-[selected=true]:ml-8',
      'group-data-[pressed=true]:w-8',
      'group-data-[selected=true]:group-data-[pressed=true]:ml-7',
    ),
    content: 'text-sm',
  },
  lg: {
    wrapper: 'w-[68px] h-8',
    thumb: cn(
      'h-8 w-8 text-lg',
      'group-data-[selected=true]:ml-9',
      'group-data-[pressed=true]:w-9',
      'group-data-[selected=true]:group-data-[pressed=true]:ml-8',
    ),
    content: 'text-xl',
  },
};

export const IconToggle = ({ size = 'lg', isSelected, onValueChange, enableIcon, disableIcon }: IconToggleProps) => {
  const config = sizeConfig[size];

  return (
    <Switch
      color="default"
      isSelected={isSelected}
      onValueChange={onValueChange}
      startContent={disableIcon}
      endContent={enableIcon}
      thumbIcon={({ isSelected: value }) => (value ? enableIcon : disableIcon)}
      classNames={{
        base: 'group',
        wrapper: cn('m-0 px-0 bg-base-60 group-data-[selected=true]:bg-base-60', config.wrapper),
        thumb: cn('bg-base-5 text-base-90', config.thumb),
        startContent: cn('left-2', config.content),
        endContent: cn('text-base-5 right-2', config.content),
      }}
    />
  );
};
